import { PlatformMark } from "@/components/brand/PlatformMark";
import { useAuth } from "@/features/auth/AuthProvider";
import { TRAINER_CAPS, canSeeNav } from "@/features/auth/capabilities";
import { settingsApi } from "@/features/settings/api";
import { hydrateTenantLogo, rememberedLogo } from "@/features/settings/logo-memory";
import { t } from "@/lib/i18n";
import { A } from "@solidjs/router";
import {
  Activity,
  Award,
  BookOpen,
  Briefcase,
  Building2,
  CalendarCheck,
  CalendarDays,
  ChartNoAxesCombined,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  GraduationCap,
  LayoutDashboard,
  LogOut,
  Receipt,
  School,
  Settings,
  ShieldCheck,
  Target,
  Users,
} from "lucide-solid";
import { For, Show, createEffect, createMemo, createResource } from "solid-js";

const NAV_GROUPS = [
  {
    label: "Pilotage",
    items: [
      { to: "/platform/schools", label: "Écoles", icon: School, platformOnly: true },
      { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard },
      { to: "/calendar", label: "Calendrier", icon: CalendarDays, anyOf: ["sessions.view"] },
      { to: "/opportunities", label: "Suivi", icon: Target, anyOf: ["pipeline.view"] },
    ],
  },
  {
    label: "Formation",
    items: [
      { to: "/formations", label: t("nav.formations"), icon: BookOpen, anyOf: ["formations.view"] },
      { to: "/trainers", label: "Formateurs", icon: GraduationCap, anyOf: TRAINER_CAPS },
      { to: "/sessions", label: t("nav.sessions"), icon: CalendarCheck, anyOf: ["sessions.view"] },
      { to: "/learners", label: t("nav.learners"), icon: Users, anyOf: ["learners.view"] },
      {
        to: "/enrollments",
        label: t("nav.enrollments"),
        icon: ClipboardList,
        anyOf: ["enrollments.view"],
      },
    ],
  },
  {
    label: "Gestion",
    items: [
      { to: "/employers", label: t("nav.employers"), icon: Briefcase, anyOf: ["employers.edit"] },
      { to: "/invoices", label: t("nav.invoices"), icon: FileText, anyOf: ["invoices.view"] },
      {
        to: "/rooms",
        label: "Salles",
        icon: Building2,
        anyOf: ["rooms.qr", "rooms.edit", "sessions.edit"],
      },
      {
        to: "/partners",
        label: "Partenaires",
        icon: Award,
        anyOf: ["formations.view", "employers.edit"],
      },
    ],
  },
  {
    label: "Conformité",
    items: [
      {
        to: "/reports/budget",
        label: t("nav.budget_report"),
        icon: ChartNoAxesCombined,
        anyOf: ["reports.budget"],
      },
      { to: "/reports/tva", label: t("nav.tva_report"), icon: Receipt, anyOf: ["reports.tva"] },
      { to: "/reports/eduqua", label: "Audit eduQua", icon: ShieldCheck, anyOf: ["eduqua.view"] },
      { to: "/health", label: t("nav.health"), icon: Activity, anyOf: ["settings.edit"] },
    ],
  },
  {
    label: "Configuration",
    items: [
      { to: "/settings", label: "Paramètres centre", icon: Settings, anyOf: ["settings.edit"] },
      { to: "/settings/access", label: "Habilitations", icon: ShieldCheck, adminOnly: true },
      { to: "/import", label: "Import Excel", icon: FileSpreadsheet, anyOf: ["learners.import"] },
    ],
  },
];

const ROLE_LABEL: Record<string, string> = {
  admin: "Direction",
  gestionnaire: "Gestion",
  formateur: "Formateur",
};

/**
 * Bloc de branding tenant dans la sidebar.
 *
 * WHY (multi-tenant CLAUDE.md §4) : ne rien hardcoder sur "Scola" — chaque
 * tenant voit son propre logo et son propre nom. Fail-safe : si l'appel
 * `/settings` échoue (réseau, JWT expiré, RLS), on retombe silencieusement
 * sur le branding générique Scola — pas d'erreur visible à l'utilisateur final
 * (charte d'excellence §6 : fail-fast côté code, fail-safe côté UX pour le
 * branding non-critique).
 */
export function SidebarBrand(props: { name?: string; tagline?: string } = {}) {
  const [settings] = createResource(async () => {
    try {
      return await settingsApi.get();
    } catch (err) {
      // Observabilité (charte §7) : remonté en console, jamais avalé silencieusement.
      console.error("[SidebarBrand] failed to load tenant settings", err);
      return null;
    }
  });

  const tenantName = () => props.name ?? settings()?.name ?? "Scola";
  const tagline = () => props.tagline ?? settings()?.tagline ?? "Formation continue CH";
  const logoUrl = () =>
    rememberedLogo(settings()?.id) ??
    settingsApi.logoPublicUrl(settings()?.logo_storage_path ?? null);

  createEffect(() => {
    const current = settings();
    if (!current?.id) return;
    void hydrateTenantLogo(current.id, () => settingsApi.fetchLogo()).catch(() => {});
  });
  return (
    <div class="flex items-center gap-3">
      <Show
        when={logoUrl()}
        fallback={<PlatformMark class="h-10 w-10 shrink-0" testId="sidebar-brand-mark" />}
      >
        {(url) => (
          <div class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-white ring-1 ring-[var(--sidebar-line)]">
            <img
              src={url()}
              alt={`${tenantName()} logo`}
              class="max-h-8 max-w-8 object-contain"
              data-testid="sidebar-brand-logo"
            />
          </div>
        )}
      </Show>
      <div class="min-w-0">
        <p class="truncate font-display text-[17px] font-semibold leading-none tracking-[-0.01em] text-[var(--sidebar-fg)]">
          {tenantName()}
        </p>
        <p class="mt-1 truncate text-[11px] text-[var(--sidebar-fg-muted)]">{tagline()}</p>
      </div>
    </div>
  );
}

function useVisibleNav() {
  const auth = useAuth();
  return createMemo(() =>
    NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        canSeeNav(item.to, auth.role(), auth.capabilities(), item, auth.platformAdmin())
      ),
    })).filter((group) => group.items.length > 0)
  );
}

export function Sidebar() {
  const auth = useAuth();
  const groups = useVisibleNav();

  return (
    <aside class="app-sidebar sticky top-0 hidden h-dvh w-[260px] shrink-0 lg:flex lg:flex-col">
      <div class="px-5 pb-4 pt-6">
        <SidebarBrand
          name={auth.platformAdmin() ? "Scola" : undefined}
          tagline={auth.platformAdmin() ? "Éditeur" : undefined}
        />
      </div>

      <nav class="flex-1 overflow-y-auto px-3 pb-4">
        <For each={groups()}>
          {(group) => (
            <section class="mt-5 first:mt-1">
              <p class="px-3 text-[10.5px] font-bold uppercase tracking-[0.16em] text-[var(--sidebar-fg-subtle)]">
                {group.label}
              </p>
              <div class="mt-1.5 space-y-px">
                <For each={group.items}>
                  {(item) => (
                    <A
                      href={item.to}
                      class="group relative flex items-center gap-3 rounded-[10px] px-3 py-[9px] text-[13.5px] font-medium text-[var(--sidebar-fg-muted)] transition duration-150 hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-fg)]"
                      activeClass="active bg-[var(--sidebar-active)] text-[var(--sidebar-active-fg)]"
                    >
                      <span class="absolute left-0 top-1/2 h-0 w-[3px] -translate-y-1/2 rounded-r-full bg-[var(--accent-base)] shadow-[0_0_12px_var(--accent-glow)] transition-all duration-200 group-[.active]:h-5" />
                      <item.icon
                        size={17}
                        stroke-width={1.75}
                        class="shrink-0 text-[var(--sidebar-fg-subtle)] transition group-hover:text-[var(--sidebar-fg)] group-[.active]:text-[var(--sidebar-active-fg)]"
                      />
                      <span class="truncate">{item.label}</span>
                    </A>
                  )}
                </For>
              </div>
            </section>
          )}
        </For>
      </nav>

      <div class="border-t border-[var(--sidebar-line)] p-3">
        <div class="flex items-center gap-3 rounded-[12px] px-2 py-2">
          <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--sidebar-hover)] text-[11px] font-bold uppercase text-[var(--sidebar-fg)] ring-1 ring-[var(--sidebar-line)]">
            {(auth.user()?.email ?? "—").slice(0, 2)}
          </div>
          <div class="min-w-0 flex-1">
            <p class="truncate text-[12.5px] font-semibold text-[var(--sidebar-fg)]">
              {auth.user()?.email ?? "—"}
            </p>
            <p class="truncate text-[11px] text-[var(--sidebar-fg-subtle)]">
              {ROLE_LABEL[auth.role() ?? ""] ?? "Session active"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => auth.signOut()}
            aria-label={t("auth.logout")}
            title={t("auth.logout")}
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[var(--sidebar-fg-subtle)] transition hover:bg-[var(--sidebar-hover)] hover:text-[var(--sidebar-fg)]"
          >
            <LogOut size={15} stroke-width={1.75} />
          </button>
        </div>
      </div>
    </aside>
  );
}

export function MobileNav() {
  const groups = useVisibleNav();
  const items = createMemo(() =>
    groups()
      .flatMap((group) => group.items)
      .slice(0, 5)
  );

  return (
    <nav class="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-[var(--border-default)] bg-[var(--bg-surface)]/95 px-1 pb-[max(env(safe-area-inset-bottom),0.25rem)] pt-1 shadow-[0_-8px_24px_rgba(11,18,32,0.08)] backdrop-blur lg:hidden">
      <For each={items()}>
        {(item) => (
          <A
            href={item.to}
            class="flex min-h-14 flex-col items-center justify-center gap-1 rounded-[10px] px-1 text-[10px] font-semibold text-[var(--fg-muted)]"
            activeClass="text-[var(--accent-alt)]"
          >
            <item.icon size={19} stroke-width={1.8} />
            <span class="max-w-full truncate">{item.label}</span>
          </A>
        )}
      </For>
    </nav>
  );
}
